import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth, homeFor } from '../state/AuthContext.jsx';
import { useMeta } from '../state/MetaContext.jsx';
import { getOfflineSyncStatus, startOfflineSync, subscribeOfflineSyncStatus } from '../api/client.js';
import NotificationBell from './NotificationBell.jsx';
import { Avatar } from './ui.jsx';
import { BrandLogo, Icon } from './icons.jsx';

/*
 * The signed-in navigation, in two levels. Each section is one link in the top
 * bar; its pages are listed under it in the sidebar. `end` pages only match
 * their exact address, so the Overview of a section does not light up on every
 * page below it.
 */
const LANDLORD_SECTIONS = [
  {
    label: 'Dashboard',
    items: [
      { to: '/landlord', label: 'Overview', icon: 'grid', end: true },
      { to: '/landlord/applications', label: 'Applications', icon: 'clipboard', capability: 'portfolio' },
      { to: '/messages', label: 'Messages', icon: 'chat' }
    ]
  },
  {
    label: 'Properties',
    items: [
      { to: '/landlord/properties', label: 'All properties', icon: 'building', capability: 'portfolio' },
      { to: '/landlord/properties/new', label: 'Add a property', icon: 'plus', capability: 'portfolio' },
      { to: '/my/land', label: 'My land', icon: 'gauge', capability: 'portfolio' },
      { to: '/landlord/offers', label: 'Offers to buy', icon: 'wallet', capability: 'portfolio' },
      { to: '/my/offers', label: 'Offers I made', icon: 'key', landlordOnly: true }
    ]
  },
  {
    label: 'Tenants',
    items: [
      { to: '/landlord/tenants', label: 'Tenants', icon: 'users', capability: 'leases_rent' },
      { to: '/landlord/leases', label: 'Leases', icon: 'file', capability: 'leases_rent' },
      { to: '/landlord/notices', label: 'Notices', icon: 'megaphone', capability: 'leases_rent' }
    ]
  },
  {
    label: 'Payments',
    items: [
      { to: '/landlord/rent', label: 'Rent', icon: 'wallet', capability: 'leases_rent' },
      { to: '/landlord/payment-accounts', label: 'Payment accounts', icon: 'card', landlordOnly: true },
      { to: '/money-log', label: 'Money log', icon: 'list', fullAccessOnly: true }
    ]
  },
  {
    label: 'Utilities',
    items: [
      { to: '/landlord/utilities', label: 'Meters and bills', icon: 'gauge', capability: 'utilities' },
      { to: '/landlord/utilities/tasks', label: 'Worklist', icon: 'list', capability: 'utilities' }
    ]
  },
  {
    label: 'Settings',
    items: [
      { to: '/profile', label: 'Profile and settings', icon: 'gear' },
      { to: '/landlord/team', label: 'Team', icon: 'team', landlordOnly: true }
    ]
  }
];

const SECTIONS_BY_ROLE = {
  tenant: [
    {
      label: 'Dashboard',
      items: [
        { to: '/tenant', label: 'Overview', icon: 'grid', end: true },
        { to: '/tenant/applications', label: 'Applications', icon: 'clipboard' },
        { to: '/tenant/saved', label: 'Saved homes', icon: 'heart' },
        { to: '/my/offers', label: 'My offers', icon: 'wallet' },
        { to: '/my/land', label: 'My land', icon: 'gauge' },
        { to: '/messages', label: 'Messages', icon: 'chat' }
      ]
    },
    { label: 'My lease', items: [{ to: '/tenant/lease', label: 'My lease', icon: 'key' }] },
    {
      label: 'My rent',
      items: [
        { to: '/tenant/rent', label: 'My rent', icon: 'wallet' },
        { to: '/money-log', label: 'Payment history', icon: 'list' }
      ]
    },
    { label: 'Utilities', items: [{ to: '/my/utilities', label: 'My utilities', icon: 'bolt' }] },
    { label: 'Settings', items: [{ to: '/profile', label: 'Profile and settings', icon: 'gear' }] }
  ],
  landlord: LANDLORD_SECTIONS,
  manager: LANDLORD_SECTIONS,
  admin: [
    {
      label: 'Dashboard',
      items: [
        { to: '/admin', label: 'Overview', icon: 'grid', end: true },
        { to: '/admin/listings', label: 'Listings', icon: 'building' },
        { to: '/admin/users', label: 'Users', icon: 'users' },
        { to: '/admin/reports', label: 'Reports', icon: 'chart' },
        { to: '/admin/land', label: 'Land register', icon: 'gauge' },
        { to: '/money-log', label: 'Money log', icon: 'chart' }
      ]
    },
    {
      label: 'Settings',
      items: [
        { to: '/admin/settings', label: 'Platform settings', icon: 'shield' },
        { to: '/profile', label: 'My profile', icon: 'user' }
      ]
    }
  ]
};

const matches = (item, pathname) =>
  item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);

const AccountMenu = () => {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapper = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onClickAway = (event) => {
      if (!wrapper.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);

  return (
    <div className="account-menu" ref={wrapper}>
      <button type="button" className="account-trigger" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <Avatar name={user.name} url={user.avatarUrl} size={34} />
        <span className="account-name">{user.name.split(' ')[0]}</span>
      </button>

      {open ? (
        <div className="dropdown account-dropdown">
          <div className="account-head">
            <strong>{user.name}</strong>
            <span>{user.email}</span>
            <em>{user.role}</em>
          </div>
          <Link to={homeFor(user)} onClick={() => setOpen(false)}>
            Dashboard
          </Link>
          <Link to="/profile" onClick={() => setOpen(false)}>
            Profile and settings
          </Link>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              signOut();
              navigate('/');
            }}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
};

const Layout = () => {
  const { user, counts } = useAuth();
  const { platform } = useMeta();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [offlineStatus, setOfflineStatus] = useState(() => getOfflineSyncStatus());

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  useEffect(() => {
    const stopSync = startOfflineSync();
    const unsubscribe = subscribeOfflineSyncStatus(setOfflineStatus);
    return () => {
      unsubscribe();
      stopSync();
    };
  }, []);

  // A manager the landlord narrowed to particular work only sees that work. The
  // server refuses the rest either way (see assertRouteCapability in the API);
  // this is so a meter reader is not shown five doors they cannot open.
  const allowed = (item) =>
    (!item.landlordOnly || user.role === 'landlord') &&
    (!item.fullAccessOnly || user.accessMode !== 'restricted') &&
    (user.accessMode !== 'restricted' || !item.capability || (user.capabilities || []).includes(item.capability));

  const sections = user
    ? (SECTIONS_BY_ROLE[user.role] || [])
        .map((section) => ({ ...section, items: section.items.filter(allowed) }))
        .filter((section) => section.items.length > 0)
    : [];

  // Somebody on a lease whose account is not a tenant account - most often one
  // set up under the wrong role - still gets the two pages about their own
  // tenancy, because the lease is theirs regardless of what the account says.
  if (user && user.hasLease && user.role !== 'tenant') {
    sections.push({
      label: 'My tenancy',
      items: [
        { to: '/tenant/lease', label: 'My lease', icon: 'key' },
        { to: '/tenant/rent', label: 'My rent', icon: 'wallet' }
      ]
    });
  }

  // The page we are on is the most specific item that matches it, so the
  // utilities worklist lights up "Worklist" and not "Meters and bills" too.
  const { pathname } = location;
  const forSale = new URLSearchParams(location.search).get('kind') === 'sale';
  let current = null;
  for (const section of sections) {
    for (const item of section.items) {
      if (matches(item, pathname) && (!current || item.to.length > current.item.to.length)) {
        current = { section, item };
      }
    }
  }
  // Public pages (the home page, search, a listing) keep the full width; the
  // sidebar is for the signed-in work.
  const showSidebar = Boolean(current);
  const showOfflineBanner = !offlineStatus.online || offlineStatus.pendingCount > 0 || Boolean(offlineStatus.lastSyncError);

  let offlineMessage = '';
  if (!offlineStatus.online) {
    offlineMessage = `Offline mode. Changes are saved on this device (${offlineStatus.pendingCount} pending sync).`;
  } else if (offlineStatus.syncing) {
    offlineMessage = `Syncing ${offlineStatus.pendingCount} saved change${offlineStatus.pendingCount === 1 ? '' : 's'}...`;
  } else if (offlineStatus.pendingCount > 0) {
    offlineMessage = `${offlineStatus.pendingCount} saved change${offlineStatus.pendingCount === 1 ? '' : 's'} waiting to sync.`;
  } else if (offlineStatus.lastSyncError) {
    offlineMessage = `Sync paused: ${offlineStatus.lastSyncError}`;
  } else if (offlineStatus.lastSyncedAt) {
    offlineMessage = `All saved changes synced at ${new Date(offlineStatus.lastSyncedAt).toLocaleTimeString()}.`;
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="site-header">
        <div className="container site-header-inner">
          <Link to="/" className="brand" aria-label={`${platform?.name || 'Cameroon Homes'} home`}>
            <BrandLogo name={platform?.name} />
          </Link>

          <nav
            className={`site-nav ${menuOpen ? 'is-open' : ''} ${showSidebar ? 'has-sidebar' : ''}`}
            aria-label="Main"
          >
            <NavLink to="/" end className="nav-link nav-home" aria-label="Home">
              <Icon name="home" size={22} />
            </NavLink>
            <Link
              to="/search"
              className={`nav-link ${pathname === '/search' && !forSale ? 'active' : ''}`}
            >
              Rent
            </Link>
            <Link
              to="/search?kind=sale"
              className={`nav-link ${pathname === '/search' && forSale ? 'active' : ''}`}
            >
              Buy
            </Link>
            {/* Marketing links only for visitors. */}
            {user ? null : (
              <NavLink to="/how-it-works" className="nav-link">
                How it works
              </NavLink>
            )}
            {!user ? (
              <NavLink to="/register?role=landlord" className="nav-link">
                List your property
              </NavLink>
            ) : null}

            {sections.map((section) => (
              <Link
                key={section.label}
                to={section.items[0].to}
                className={`nav-link nav-section ${current?.section === section ? 'active' : ''}`}
                aria-current={current?.section === section ? 'page' : undefined}
              >
                {section.label}
                {section.items.some((item) => item.to === '/messages') && counts.messages > 0 ? (
                  <span className="nav-count">{counts.messages}</span>
                ) : null}
              </Link>
            ))}
          </nav>

          <div className="header-actions">
            {user ? (
              <>
                <NotificationBell />
                <AccountMenu />
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-sm">
                  Sign in
                </Link>
                <Link to="/register" className="btn btn-primary btn-sm">
                  Create account
                </Link>
              </>
            )}

            <button
              type="button"
              className="menu-toggle"
              onClick={() => setMenuOpen((value) => !value)}
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>

        {platform?.announcement ? <div className="announcement">{platform.announcement}</div> : null}
        {showOfflineBanner ? <div className="offline-sync-banner">{offlineMessage}</div> : null}
      </header>

      {showSidebar ? (
        <div className="workspace">
          <aside className={`app-sidebar ${menuOpen ? 'is-open' : ''}`} aria-label="Section">
            {/* On a phone the top bar's links fold in here, so the open menu is
                one list rather than two stacked over each other. */}
            <div className="sidebar-group sidebar-public">
              <Link to="/" className="sidebar-link">
                <Icon name="home" />
                <span>Home</span>
              </Link>
              <Link to="/search" className="sidebar-link">
                <Icon name="search" />
                <span>Rent a home</span>
              </Link>
              <Link to="/search?kind=sale" className="sidebar-link">
                <Icon name="key" />
                <span>Buy a house or land</span>
              </Link>
              <Link to="/land" className="sidebar-link">
                <Icon name="gauge" />
                <span>Land register</span>
              </Link>
            </div>
            {sections.map((section) => (
              <div
                key={section.label}
                className={`sidebar-group ${current.section === section ? 'is-current' : ''}`}
              >
                {/* A section of one page is named by that page already. */}
                {section.items.length > 1 ? <span className="sidebar-label">{section.label}</span> : null}
                {section.items.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`sidebar-link ${current.item === item ? 'active' : ''}`}
                    aria-current={current.item === item ? 'page' : undefined}
                  >
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                    {item.to === '/messages' && counts.messages > 0 ? (
                      <span className="nav-count">{counts.messages}</span>
                    ) : null}
                  </Link>
                ))}
              </div>
            ))}
          </aside>
          <main id="main" className="site-main">
            <Outlet />
          </main>
        </div>
      ) : (
        <main id="main" className="site-main">
          <Outlet />
        </main>
      )}

      <footer className="site-footer">
        <div className="container footer-grid">
          <div>
            <span className="footer-brand">
              <BrandLogo name={platform?.name} />
            </span>
            <p>
              Houses, flats and rooms to rent across Cameroon. Deal directly with the landlord, apply online, and keep
              every document in one place.
            </p>
          </div>

          <div>
            <h4>For tenants</h4>
            <Link to="/search">Browse listings</Link>
            <Link to="/search?sort=price_asc">Cheapest first</Link>
            <Link to="/how-it-works">How applying works</Link>
            <Link to="/track">Track an application</Link>
            <Link to="/land">Check a plot on the land register</Link>
          </div>

          <div>
            <h4>For landlords</h4>
            <Link to="/register?role=landlord">Create a landlord account</Link>
            <Link to="/how-it-works#landlords">Listing guidelines</Link>
          </div>

          <div>
            <h4>Support</h4>
            {platform?.supportEmail || platform?.supportPhone ? (
              <p className="muted">
                {platform?.supportEmail ? (
                  <a href={`mailto:${platform.supportEmail}`}>{platform.supportEmail}</a>
                ) : null}
                {platform?.supportEmail && platform?.supportPhone ? <br /> : null}
                {platform?.supportPhone ? <a href={`tel:${platform.supportPhone}`}>{platform.supportPhone}</a> : null}
              </p>
            ) : (
              <p className="muted">Support contact details are available to authorized users after sign-in.</p>
            )}
          </div>
        </div>

        <div className="container footer-bottom">
          <span>All prices are shown in XAF (FCFA).</span>
          <span>&copy; {new Date().getFullYear()} {platform?.name || 'Cameroon Homes'}</span>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
